import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import {
	pluginKey,
	type AiManagedObjectLoadStatus,
	type AgentManagedExtensionPluginState,
} from '../../common/agentManagedExtensionPluginType';

export const getCachedAiManagedObjectFromPluginState = (
	pluginState: AgentManagedExtensionPluginState | undefined,
	aimoId: string,
): AiManagedObject | undefined => pluginState?.aiManagedObjectsById?.[aimoId];

export const getCachedAiManagedObjectFromState = (
	state: EditorState,
	aimoId: string,
): AiManagedObject | undefined =>
	getCachedAiManagedObjectFromPluginState(pluginKey.getState(state), aimoId);

export const getAiManagedObjectLoadStatusFromPluginState = (
	pluginState: AgentManagedExtensionPluginState | undefined,
	aimoId: string | undefined,
): AiManagedObjectLoadStatus | undefined =>
	aimoId ? pluginState?.aiManagedObjectLoadStatusById?.[aimoId] : undefined;

export const getAiManagedObjectLoadStatusFromState = (
	state: EditorState,
	aimoId: string | undefined,
): AiManagedObjectLoadStatus | undefined =>
	getAiManagedObjectLoadStatusFromPluginState(pluginKey.getState(state), aimoId);

export const getCachedAiManagedObjectByLocalIdFromPluginState = (
	pluginState: AgentManagedExtensionPluginState | undefined,
	localId: string | undefined,
): AiManagedObject | undefined => {
	const aimoId = localId ? pluginState?.aiManagedObjectIdsByLocalId[localId] : undefined;
	return aimoId ? getCachedAiManagedObjectFromPluginState(pluginState, aimoId) : undefined;
};

export const getAiManagedObjectLoadStatusByLocalIdFromPluginState = (
	pluginState: AgentManagedExtensionPluginState | undefined,
	localId: string | undefined,
): AiManagedObjectLoadStatus | undefined => {
	const aimoId = localId ? pluginState?.aiManagedObjectIdsByLocalId[localId] : undefined;
	return getAiManagedObjectLoadStatusFromPluginState(pluginState, aimoId);
};

export const cacheAiManagedObjectInPluginState = (
	prev: AgentManagedExtensionPluginState,
	aiManagedObject: AiManagedObject,
	localId?: string,
): AgentManagedExtensionPluginState => {
	const { [aiManagedObject.id]: _removedLoadStatus, ...aiManagedObjectLoadStatusById } =
		prev.aiManagedObjectLoadStatusById;

	return {
		...prev,
		aiManagedObjectIdsByLocalId: localId
			? {
					...prev.aiManagedObjectIdsByLocalId,
					[localId]: aiManagedObject.id,
				}
			: prev.aiManagedObjectIdsByLocalId,
		aiManagedObjectsById: {
			...prev.aiManagedObjectsById,
			[aiManagedObject.id]: aiManagedObject,
		},
		aiManagedObjectLoadStatusById,
	};
};

export const setAiManagedObjectLoadStatusInPluginState = (
	prev: AgentManagedExtensionPluginState,
	aimoId: string,
	status: AiManagedObjectLoadStatus,
	localId?: string,
): AgentManagedExtensionPluginState => ({
	...prev,
	aiManagedObjectIdsByLocalId: localId
		? {
				...prev.aiManagedObjectIdsByLocalId,
				[localId]: aimoId,
			}
		: prev.aiManagedObjectIdsByLocalId,
	aiManagedObjectLoadStatusById: {
		...prev.aiManagedObjectLoadStatusById,
		[aimoId]: status,
	},
});
